import { FC } from 'react';
import { css } from '@emotion/react';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { generateTrackingId } from '@frontend/call-intel-core';
import { Divider } from '@frontend/divider';
import { useTranslation, i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, Text, TextLink } from '@frontend/design-system';
import { isContentSearchType } from '../../../../utils/call-intelligence';
import { CallSearchTypeDropdown } from './call-search-type-dropdown';

const TRACKING_COMPONENT = 'suggested-search';

const suggestions = [
  {
    label: i18next.t('Cancel appointment', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'cancel-appointment' }),
  },
  {
    label: i18next.t('Reschedule appointment', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'reschedule-appointment' }),
  },
  {
    label: i18next.t('Pricing', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'pricing' }),
  },
  {
    label: i18next.t('Payment', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'payment' }),
  },
  {
    label: i18next.t('Billing issues', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'billing-issues' }),
  },
  {
    label: i18next.t('Insurance issue', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'insurance-issue' }),
  },
  {
    label: i18next.t('Promised follow up', { ns: 'call-intelligence' }),
    trackingId: generateTrackingId({ component: TRACKING_COMPONENT, context: 'promised-follow-up' }),
  },
] as const;

type SuggestedSearchItem = {
  label: string;
  trackingId: string;
};

type SuggestedSearchChipProps = {
  item: SuggestedSearchItem;
  onClick: () => void;
};

type SuggestedSearchesProps = {
  isSmallMode?: boolean;
  isSearchQueryEmpty: boolean;
  searchType: CallIntelTypes.SearchTypeEnum;
  searchTypeMenuRef: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
  errorMessage?: string;
  onSelect: (query: string) => void;
  onSearchTypeChange: (searchType: CallIntelTypes.SearchTypeEnum) => void;
};

const TRANSCRIPT_HELP_LINK =
  'https://www.weavehelp.com/weavehelp/article/call-intelligence-all-call-recordings-tab#Transcript_Search_-_Beta';

const SuggestedSearchChip: FC<SuggestedSearchChipProps> = ({ item, onClick }) => (
  <Chip
    css={styles.chip}
    trackingId={item.trackingId}
    leftElement={<Icon name='aiassistant-small' color='default' />}
    onClick={onClick}
    variant='outline'
  >
    {item.label}
  </Chip>
);

const HelpLink = () => {
  const { t } = useTranslation('call-intelligence');

  return (
    <TextLink
      css={styles.helpLink}
      data-trackingid={generateTrackingId({ component: TRACKING_COMPONENT, context: 'help-link' })}
      rel='noopener noreferrer'
      size='medium'
      target='_blank'
      to={TRANSCRIPT_HELP_LINK}
      weight='bold'
    >
      <Icon name='help' size={16} />
      {t('Help')}
    </TextLink>
  );
};

export const SuggestedSearches: FC<SuggestedSearchesProps> = ({
  isSmallMode,
  isSearchQueryEmpty,
  searchTypeMenuRef,
  onSelect,
  searchType,
  hasError,
  errorMessage,
  onSearchTypeChange,
}) => {
  const { t } = useTranslation('call-intelligence');

  const showSuggestedSearches = isSearchQueryEmpty && isContentSearchType(searchType);

  return (
    <div aria-label={t('Suggested searches')} css={styles.container} role='listbox'>
      <div css={styles.header}>
        <div css={styles.headerRow}>
          <CallSearchTypeDropdown value={searchType} onChange={onSearchTypeChange} menuRef={searchTypeMenuRef} />
          <HelpLink />
        </div>

        <Text css={styles.helpText(!!hasError)} size='medium'>
          {hasError
            ? errorMessage
            : t(
                'Explore calls by topics or phrases from the conversation, or change your search area to search by name or phone number.'
              )}
        </Text>
      </div>

      {showSuggestedSearches && <Divider />}

      {showSuggestedSearches && (
        <div css={styles.content}>
          <Text size='medium' weight='bold'>
            {t('Suggested Searches')}
          </Text>

          <div css={isSmallMode ? styles.listLayout : styles.chipsLayout}>
            {suggestions.map((item) => (
              <SuggestedSearchChip item={item} key={item.trackingId} onClick={() => onSelect(item.label)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  chip: css`
    border: 1px solid ${theme.colors.neutral30};
    border-radius: ${theme.borderRadius.full};
    color: ${theme.colors.neutral90};
    cursor: pointer;
    gap: ${theme.spacing(0.5)};
    height: ${theme.spacing(3.5)};
    max-width: max-content;

    &:hover {
      background: ${theme.colors.neutral5};
    }
  `,
  chipsLayout: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
  `,
  container: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.floating};
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(2, 0)};
    z-index: ${theme.zIndex.popover};
  `,
  header: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(0, 2)};
  `,
  headerRow: css`
    display: flex;
    gap: ${theme.spacing(0.5)};
    justify-content: space-between;
  `,
  helpLink: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(0.5)};
    white-space: nowrap;
  `,
  listLayout: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(0, 2)};
    gap: var(--fabric-number-n-2-px);
  `,
  helpText: (hasError: boolean) => css`
    color: ${hasError ? theme.colors.critical50 : theme.colorsV2.black(75)};
  `,
};
